import { Dictionary } from '../../helpers/dictionary'
import { DataProvider } from '../../helpers'

import { IReferenceType, IOpChangeState, IJsonSerializable, ICopyable } from '../util/interfaces'
import { Enums } from '../common/enums'
import { Utils } from '../util/utils'

import { OpDescr } from './op-descr'
import { OpReferenceType } from './op-reference-type'

export class OpActivity implements IReferenceType, IOpChangeState, IJsonSerializable, ICopyable<OpActivity> {

    //#region Properties

    public idActivity: number
    public name: string
    public idDescr: number
    public idReferenceType: number

    //#endregion


    //#region NavigationProperties

    private _descr: OpDescr
    public get descr(): OpDescr {
        return this._descr
    }
    public set descr(value: OpDescr) {
        this._descr = value
        this.idDescr = value != null ? value.idDescr : null
    }

    private _referenceType: OpReferenceType
    public get referenceType(): OpReferenceType {
        return this._referenceType
    }
    public set referenceType(value: OpReferenceType) {
        this._referenceType = value
        this.idReferenceType = value != null ? value.idReferenceType : null
    }

    //#endregion


    //#region CustomProperties

    public get allow(): boolean { return !this.deny }
    public set allow(value: boolean) { this.deny = !value }

    public deny: boolean
    public idModule: number
    public referenceValue: any
    public idRoleActivity: number
    public idOperatorActivity: number

    //#endregion


    //#region Ctor

    constructor(opObj: any) {
        if (opObj instanceof OpActivity) {
            this.copy(<OpActivity>opObj, true, true, true, true)
        } else {
            this.fromJSON(opObj)
        }
    }

    //#endregion


    //#region IOpChangeState Implementation

    public opState: Enums.OpChangeState

    //#endregion


    //#region IReferenceType Implementation

    public getReferenceType(): Enums.ReferenceType {
        return Enums.ReferenceType.IdActivity
    }

    public getReferenceKey(): number {
        return this.idActivity
    }

    public getReferenceValue(): any {
        return this
    }

    //#endregion


    //#region IJsonSerializable Implementation

    public fromJSON(opObj: any, compressed: boolean = false): void {
        if (opObj) {
            if (compressed || Utils.isJSONCompressed(opObj)) {
                this.descr = opObj.D ? new OpDescr(opObj.D) : null
                this.referenceType = opObj.RT ? new OpReferenceType(opObj.RT) : null

                this.idActivity = opObj.I
                this.name = opObj.N
                this.idDescr = opObj.ID
                this.idReferenceType = opObj.IR

                this.deny = opObj.DE
                this.referenceValue = opObj.R
                this.idModule = opObj.IM
                this.idRoleActivity = opObj.IRA
                this.idOperatorActivity = opObj.IOA

                this.opState = opObj.O
            } else {
                this.descr = opObj._Descr ? new OpDescr(opObj._Descr) : null
                this.referenceType = opObj._ReferenceType ? new OpReferenceType(opObj._ReferenceType) : null

                this.idActivity = opObj.ID_ACTIVITY
                this.name = opObj.NAME
                this.idDescr = opObj.ID_DESCR
                this.idReferenceType = opObj.ID_REFERENCE_TYPE

                this.deny = opObj.Deny
                this.referenceValue = opObj.ReferenceValue
                this.idModule = opObj.IdModule
                this.idRoleActivity = opObj.IdRoleActivity
                this.idOperatorActivity = opObj.IdOperatorActivity
                this.opState = opObj.OpState
            }
        }
    }

    public toJSON(compressed: boolean = false): any {

        if (compressed) {
            const result = {
                I: this.idActivity,
                ID: this.idDescr,
                IR: this.idReferenceType,
                N: this.name,

                DE: this.deny,
                R: this.referenceValue,
                IM: this.idModule,
                IRA: this.idRoleActivity,
                IOA: this.idOperatorActivity,

                O: this.opState,
                RT: this.referenceType != null ? this.referenceType.toJSON(compressed) : null,
                D: this.descr != null ? this.descr.toJSON(compressed) : null
            }

            Utils.setJSONCompressedFlag(result)

            return result
        }

        return {
            ID_ACTIVITY: this.idActivity,
            ID_DESCR: this.idDescr,
            ID_REFERENCE_TYPE: this.idReferenceType,
            NAME: this.name,

            Deny: this.deny,
            ReferenceValue: this.referenceValue,
            IdModule: this.idModule,
            IdRoleActivity: this.idRoleActivity,
            IdOperatorActivity: this.idOperatorActivity,

            OpState: this.opState,

            _ReferenceType: this.referenceType != null ? this.referenceType.toJSON(compressed) : null,
            _Descr: this.descr != null ? this.descr.toJSON(compressed) : null
        }
    }

    //#endregion


    //#region ICopyable Implementation

    public clone(includeNavigationProperties: boolean = false, includeCustomProperties: boolean = false, includeDataList: boolean = false, includeDynamicValues: boolean = false): OpActivity {
        const newActivity = new OpActivity(null)

        newActivity.copy(this, includeNavigationProperties, includeCustomProperties, includeDataList, includeDynamicValues)

        return newActivity
    }

    public copy(source: OpActivity, includeNavigationProperties: boolean = false, includeCustomProperties: boolean = false, includeDataList: boolean = false, includeDynamicValues: boolean = false): void {
        if (source == null) {
            return null
        }

        if (includeNavigationProperties) {
            if (source.descr != null) {
                this.descr = source.descr                       // TODO: Copy methods for those types
            }

            if (source.referenceType) {
                this.referenceType = source.referenceType       // TODO: Copy methods for those types
            }
        }

        if (includeCustomProperties) {
            this.deny = source.deny
            this.idModule = source.idModule
            this.referenceValue = source.referenceValue
            this.idRoleActivity = source.idRoleActivity
            this.idOperatorActivity = source.idOperatorActivity
        }

        // No datalist
        // No dynamicValues

        this.idActivity = source.idActivity
        this.name = source.name
        this.idDescr = source.idDescr
        this.idReferenceType = source.idReferenceType

        this.opState = source.opState
    }

    //#endregion


    //#region ToString

    public toString() {
        if (this.descr != null) {
            return this.descr.description
        }

        return this.name
    }

    //#endregion

    //#region LoadNavigationProperties

    public static loadNavigationProperties(dataProvider: DataProvider, list: OpActivity[]): JQueryPromise<OpActivity[]> {
        const defered: JQueryDeferred<OpActivity[]> = $.Deferred<OpActivity[]>()

        if (dataProvider != null && list != null && list.length > 0) {
            dataProvider.System
                .getDictionaries([Enums.ReferenceType.IdReferenceType])
                .then((dict: Dictionary<Dictionary<IReferenceType>>) => {
                    let referenceTypeDict: Dictionary<OpReferenceType> = new Dictionary<OpReferenceType>(null)

                    if (dict.containsKey(Enums.ReferenceType.IdReferenceType)) {
                        referenceTypeDict = <Dictionary<OpReferenceType>>dict.tryGetValue(Enums.ReferenceType.IdReferenceType)
                    }

                    list.forEach((item: OpActivity) => {
                        item.referenceType = referenceTypeDict.tryGetTypedValue<OpActivity>(item.idReferenceType)
                    })

                    defered.resolve(list)
                })
        } else {
            defered.resolve(list)
        }

        return defered.promise()
    }

    //#endregion
}